import { wrap } from '@popmotion/popcorn';
import { AnimatePresence, motion } from 'framer-motion';
import { forwardRef, memo, useEffect, useRef, useState } from 'react';
import { CarouselType } from './CarouselTypes';
import { CarouselNavigation } from './Components/CarouselNavigation';
import { AnimationVariants, swipeConfidenceThreshold, SwipePower } from './CarouselSettings';

const Carousel = memo(
	forwardRef((props: CarouselType, ref: React.ForwardedRef<HTMLDivElement>) => {
		const [[page, direction], setPage] = useState([0, 0]);

		const contentIndex = wrap(0, props.items.length, page);

		const canvasWrapperRef = useRef<HTMLDivElement>(null);
		const canvasInnerWrapperRef = useRef<HTMLDivElement>(null);

		const Paginate = (newDirection: number) => {
			setPage([page + newDirection, newDirection]);
		};

		const JumpTo = (index: number) => {
			setPage([index, page > index ? 0 : 1]);
		};

		const UpdateContainerHeight = () => {
			if (
				canvasWrapperRef.current &&
				canvasInnerWrapperRef.current &&
				canvasInnerWrapperRef.current.querySelector('div')
			) {
				canvasWrapperRef.current.style.height = `${canvasInnerWrapperRef.current.querySelector('div')?.getBoundingClientRect().height}px`;
			}
		};

		useEffect(() => {
			UpdateContainerHeight();
		}, [props, canvasWrapperRef.current, canvasInnerWrapperRef.current, page, direction]);

		return (
			<div className={`carousel-wrapper ${props.isOpen ? '' : 'hide'}`} style={props.style} ref={ref}>
				{props.title && (
					<div className='carousel-title'>
						<div>{props.title}</div>

						<CarouselNavigation
							items={props.items}
							index={contentIndex}
							navigationCallback={(val: number) => JumpTo(val)}
							paginateCallback={(val: number) => Paginate(val)}
						/>
					</div>
				)}

				<div className='carousel' ref={canvasWrapperRef}>
					<AnimatePresence initial={false} custom={direction}>
						<motion.div
							key={`${props.title}${page}`}
							ref={canvasInnerWrapperRef}
							custom={direction}
							variants={AnimationVariants}
							initial='enter'
							animate='center'
							exit='exit'
							transition={{
								x: { type: 'spring', stiffness: 300, damping: 30 },
								opacity: { duration: 0.1 },
							}}
							drag='x'
							dragConstraints={{ left: 0, right: 0 }}
							dragElastic={1}
							onDragEnd={(e, { offset, velocity }) => {
								const swipe = SwipePower(offset.x, velocity.x);

								if (swipe < -swipeConfidenceThreshold) {
									Paginate(1);
								} else if (swipe > swipeConfidenceThreshold) {
									Paginate(-1);
								}
							}}
						>
							{props.items[contentIndex]}
						</motion.div>
					</AnimatePresence>

					{!props.title && (
						<CarouselNavigation
							items={props.items}
							index={contentIndex}
							navigationCallback={(val: number) => JumpTo(val)}
							paginateCallback={(val: number) => Paginate(val)}
						/>
					)}
				</div>
			</div>
		);
	}),
);

export default Carousel;
